/**
 * What the campaign screen shows (client change of 2026-10-30): one campaign, its incidents, its plan and its
 * own figures for the seven pipeline stages. Built from the rows the Threat Feed already builds, so the screen
 * and the feed never disagree about which incidents form a campaign (item 3).
 *
 * A lone incident is a campaign of one for this screen: the feed shows it as an incident and the console's
 * counters do not count it as a campaign, but it opens here like any campaign.
 *
 * The plan is the plans of the campaign's incidents. The backend stores one remediation per incident
 * (`threat_event_id`) and has no campaign field, so the campaign's plan is rolled up here: one status, one
 * count of what waits on a person. Nothing is written back (docs/c3/BACKEND.md, plan per campaign).
 *
 * Pure: no request, no clock.
 */
import type { FunnelCounters } from '../../lib/funnel';
import type { RemediationItem, SecurityEventItem } from '../domain/types';
import { normalize, remMode, type FeedInputs, type FeedRow, type FeedState } from './feed';
import { buildFeedModel } from './feed';
import { computeFunnelCounters } from './funnelCounters';

export interface CampaignInputs extends FeedInputs {
  securityEvents: SecurityEventItem[];
}

export type PlanStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'reverted' | 'none';

export interface CampaignPlanItem {
  threatId: string;
  title: string;
  state: FeedState;
  plan: RemediationItem | null;
  /** `none` when the incident has no plan yet. */
  status: PlanStatus;
  mode: 'auto' | 'gated' | '';
  /** The plan's steps, as the backend wrote them. */
  steps: string[];
  /** A person has to decide this one: a gated plan that is pending or confirmed. */
  needsDecision: boolean;
}

export interface CampaignPlan {
  items: CampaignPlanItem[];
  /** Incidents per plan status; `none` counts those without a plan. */
  counts: Record<PlanStatus, number>;
  /** Plans that exist. */
  total: number;
  /** Incidents that wait on a person. */
  awaiting: number;
  /** One line for the plan as a whole, from the counts. */
  summary: string;
}

export interface CampaignView {
  /** The id the screen is addressed by: the campaign's, or the incident's for a campaign of one. */
  id: string;
  kind: 'campaign' | 'single';
  row: FeedRow;
  plan: CampaignPlan;
  /** The same counters the Overview uses, counted over this campaign's records only, over all time. */
  counters: FunnelCounters;
}

const PLAN_STATUSES: PlanStatus[] = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled', 'reverted'];

function toPlanStatus(plan: RemediationItem | null): PlanStatus {
  if (!plan) return 'none';
  const status = normalize(plan.status).toLowerCase();
  return (PLAN_STATUSES as string[]).includes(status) ? (status as PlanStatus) : 'pending';
}

/** The words of one step, whatever shape the backend gave it: text, or a record with a title. */
export function stepLabel(step: unknown): string {
  if (typeof step === 'string') return step.trim();
  if (step && typeof step === 'object') {
    const record = step as Record<string, unknown>;
    for (const key of ['step', 'title', 'name', 'action', 'label']) {
      const text = normalize(record[key]);
      if (text) return text;
    }
  }
  return '';
}

export function buildCampaignPlan(row: FeedRow): CampaignPlan {
  const items: CampaignPlanItem[] = row.members.map((member) => {
    const plan = member.threat.plan;
    return {
      threatId: member.threat.event._id,
      title: member.title,
      state: member.state,
      plan,
      status: toPlanStatus(plan),
      mode: remMode(plan),
      steps: (Array.isArray(plan?.operations) ? plan!.operations : []).map(stepLabel).filter(Boolean),
      needsDecision: member.state.kind === 'pending_approval',
    };
  });
  const counts: Record<PlanStatus, number> = {
    pending: 0,
    confirmed: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    reverted: 0,
    none: 0,
  };
  items.forEach((item) => {
    counts[item.status] += 1;
  });
  const total = items.length - counts.none;
  const awaiting = items.filter((item) => item.needsDecision).length;
  const summary = planSummary(counts, total, items.length, awaiting);
  return { items, counts, total, awaiting, summary };
}

function planSummary(counts: Record<PlanStatus, number>, total: number, incidents: number, awaiting: number): string {
  if (total === 0) return incidents === 1 ? 'No plan yet for this incident' : 'No plan yet for these incidents';
  const plans = `${total} plan${total === 1 ? '' : 's'}`;
  if (awaiting > 0) return `${awaiting} of ${plans} await${awaiting === 1 ? 's' : ''} your decision`;
  if (counts.completed === total) return `All ${plans} completed`;
  if (counts.in_progress > 0) return `${counts.in_progress} of ${plans} in progress`;
  if (counts.reverted > 0) return `${counts.reverted} of ${plans} reverted`;
  if (counts.cancelled === total) return `All ${plans} cancelled`;
  return `${counts.completed} of ${plans} completed`;
}

/** The row an id belongs to: a campaign's own id, an incident that stands alone, or an incident inside a campaign. */
export function findCampaignRow(rows: FeedRow[], id: string): FeedRow | null {
  const wanted = id.trim();
  if (!wanted) return null;
  return (
    rows.find((row) => row.id === wanted) ??
    rows.find((row) => row.threats.some((threat) => threat.event._id === wanted)) ??
    null
  );
}

export function buildCampaignView(row: FeedRow, inputs: CampaignInputs): CampaignView {
  const threatIds = new Set(row.threats.map((t) => t.event._id));
  const candidateIds = new Set(row.threats.map((t) => t.event.candidate_id).filter(Boolean));
  const actionIds = new Set(row.threats.map((t) => t.action?._id).filter((v): v is string => Boolean(v)));

  const counters = computeFunnelCounters(
    {
      candidates: inputs.candidates.filter((c) => candidateIds.has(c._id)),
      events: inputs.securityEvents.filter(
        (e) => (e.threat_event_id && threatIds.has(e.threat_event_id)) || (e.candidate_id && candidateIds.has(e.candidate_id))
      ),
      threatEvents: inputs.threatEvents.filter((e) => threatIds.has(e._id)),
      elements: inputs.elements.filter(
        (el) => (el._id && actionIds.has(el._id)) || (row.campaign && el._id === row.campaign._id)
      ),
      remediations: inputs.remediations.filter((r) => r.threat_event_id && threatIds.has(r.threat_event_id)),
      sources: [],
    },
    Date.now(),
    'all_time'
  );

  // A campaign of one is still a campaign here, and every incident of it is in it.
  const open = row.state.kind === 'closed' || row.state.kind === 'failed' ? 0 : 1;
  counters.campaigns = { total: 1, open, closed: 1 - open };
  counters.threats.inCampaign = row.threats.length;
  counters.threats.standalone = 0;

  return {
    id: row.id,
    kind: row.kind === 'campaign' ? 'campaign' : 'single',
    row,
    plan: buildCampaignPlan(row),
    counters,
  };
}

/** Finds the campaign an id opens and builds its view; null when no incident or campaign has that id. */
export function resolveCampaignView(id: string, inputs: CampaignInputs): CampaignView | null {
  const row = findCampaignRow(buildFeedModel(inputs).rows, id);
  return row ? buildCampaignView(row, inputs) : null;
}
